import { FC, useState } from 'react';
import Drawer from '@material-ui/core/Drawer';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import Badge from '../../components/shared/Badge/Badge';
import { useTranslation } from 'react-i18next';
import { SanctionsResult, SanctionsSource, PersonSanctionsMatch } from '../../services/service-sanctions';
import Loading from '../../components/shared/Loading';
import styles from './sanctions-drawer.module.scss';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  results: SanctionsResult[];
  query: string;
  loading: boolean;
  personMatches?: PersonSanctionsMatch[];
};

const SOURCE_COLORS: Record<SanctionsSource, { bg: string; color: string }> = {
  EU: { bg: 'var(--blue-100, #dbeafe)', color: 'var(--blue-800, #1e40af)' },
  OFAC: { bg: 'var(--orange-100, #ffedd5)', color: 'var(--orange-800, #9a3412)' },
  UK: { bg: 'var(--purple-100, #f3e8ff)', color: 'var(--purple-800, #6b21a8)' },
};

const MAX_VISIBLE_ALIASES = 3;

const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();

const highlightMatches = (text: string, queryTokens: string[]) => {
  if (!queryTokens.length) return text;
  const normalizedText = normalize(text);
  // Find character ranges in normalizedText that match any token
  const ranges: { start: number; end: number }[] = [];
  for (const token of queryTokens) {
    const regex = new RegExp(`\\b${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi');
    let match;
    while ((match = regex.exec(normalizedText)) !== null) {
      ranges.push({ start: match.index, end: match.index + token.length });
    }
  }
  if (!ranges.length) return text;
  // Merge overlapping ranges
  ranges.sort((a, b) => a.start - b.start);
  const merged: { start: number; end: number }[] = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const last = merged[merged.length - 1];
    if (ranges[i].start <= last.end) {
      last.end = Math.max(last.end, ranges[i].end);
    } else {
      merged.push(ranges[i]);
    }
  }
  // Map ranges from normalizedText back to original text (same indices since normalize only changes chars, not length... actually it can change length with NFD)
  // Safer: highlight on original text using same positions (works when lengths match)
  const parts: (string | JSX.Element)[] = [];
  let pos = 0;
  for (const { start, end } of merged) {
    if (start > pos) parts.push(text.slice(pos, start));
    parts.push(<mark key={start} style={{ backgroundColor: '#fef08a', padding: '0 1px', borderRadius: 2 }}>{text.slice(start, end)}</mark>);
    pos = end;
  }
  if (pos < text.length) parts.push(text.slice(pos));
  return <>{parts}</>;
};

const SanctionsDrawer: FC<Props> = ({ isOpen, onClose, results, query, loading, personMatches = [] }) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'supplierControls.sanctions.drawer' });
  const [expandedPersons, setExpandedPersons] = useState<Set<number>>(new Set());
  const queryTokens = normalize(query).split(/\s+/).filter(t => t.length >= 3);

  const togglePerson = (index: number) => {
    setExpandedPersons(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const renderResult = (result: SanctionsResult) => {
    const visibleAliases = result.aliases.slice(0, MAX_VISIBLE_ALIASES);
    const hiddenCount = result.aliases.length - MAX_VISIBLE_ALIASES;
    const sourceColor = SOURCE_COLORS[result.source];

    return (
      <div key={result.id} className={styles.resultItem}>
        <div className={styles.resultHeader}>
          <span className={styles.resultName}>{highlightMatches(result.primaryName, queryTokens)}</span>
          <div className={styles.badges}>
            <Badge backgroundColor={sourceColor.bg} color={sourceColor.color}>
              {result.source}
            </Badge>
            <span className={styles.entityBadge}>
              {result.entityType === 'person'
                ? t('entityType.person', 'Person')
                : result.entityType === 'entity'
                  ? t('entityType.entity', 'Organisation')
                  : t('entityType.unknown', 'Okänd typ')}
            </span>
          </div>
        </div>

        {visibleAliases.length > 0 && (
          <div className={styles.detail}>
            <span className={styles.detailLabel}>{t('aliases', 'Alias')}: </span>
            <span className={styles.aliases}>{visibleAliases.map((alias, i) => <span key={i}>{i > 0 ? ', ' : ''}{highlightMatches(alias, queryTokens)}</span>)}</span>
            {hiddenCount > 0 && (
              <span className={styles.moreAliases}> +{hiddenCount}</span>
            )}
          </div>
        )}

        {result.programs.length > 0 && (
          <div className={styles.detail}>
            <span className={styles.detailLabel}>{t('program', 'Program')}: </span>
            {result.programs.join(', ')}
          </div>
        )}

        {result.country && (
          <div className={styles.detail}>
            <span className={styles.detailLabel}>{t('country', 'Land')}: </span>
            {result.country}
          </div>
        )}
      </div>
    );
  };

  const renderPersonMatch = (person: PersonSanctionsMatch, index: number) => {
    const isExpanded = expandedPersons.has(index);

    return (
      <div key={`person-${index}`} className={styles.personMatch}>
        <button
          className={styles.personHeader}
          onClick={() => togglePerson(index)}
        >
          <div className={styles.personInfo}>
            <span className={styles.personName}>{person.personName}</span>
            <div className={styles.roleBadges}>
              {person.functionsSwedish.map((fn, i) => (
                <span key={i} className={styles.roleBadge}>{fn}</span>
              ))}
            </div>
          </div>
          <div className={styles.personToggle}>
            <span className={styles.personMatchCount}>
              {person.sanctionsMatches.length === 1 ? t('personMatchCountOne', '1 träff') : t('personMatchCount', '{{count}} träffar', { count: person.sanctionsMatches.length })}
            </span>
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </div>
        </button>
        {isExpanded && (
          <div className={styles.personResults}>
            {person.sanctionsMatches.map(renderResult)}
          </div>
        )}
      </div>
    );
  };

  const hasPersonMatches = personMatches.length > 0;

  return (
    <Drawer anchor="right" open={isOpen} onClose={onClose}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h3>
            {t('heading', 'Sanktionslistor')}
            {query && <span className={styles.companyName}>{query}</span>}
          </h3>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        {loading ? (
          <div className={styles.empty}>
            <Loading type="inline" loadingStyle="bubbles" size="30px" />
          </div>
        ) : (
          <>
            {/* Person matches section */}
            {hasPersonMatches && (
              <>
                <div className={styles.sectionHeader}>
                  {t('personSection', 'Nyckelpersoner')}
                  <span className={styles.sectionCount}>
                    {personMatches.length === 1 ? t('personSectionCountOne', '1 person med träffar') : t('personSectionCount', '{{count}} personer med träffar', { count: personMatches.length })}
                  </span>
                </div>
                <div className={styles.personList}>
                  {personMatches.map(renderPersonMatch)}
                </div>
              </>
            )}

            {/* Company matches section */}
            <div className={styles.sectionHeader}>
              {hasPersonMatches
                ? t('companySection', 'Företagsnamn')
                : null}
              <span className={styles.sectionCount}>
                {results.length > 0
                  ? (results.length === 1 ? t('summaryOne', '1 träff för "{{query}}"', { query }) : t('summary', '{{count}} träffar för "{{query}}"', { count: results.length, query }))
                  : t('noResults', 'Inga träffar')}
              </span>
            </div>
            <div className={styles.resultsList}>
              {results.length > 0
                ? results.map(renderResult)
                : (
                  <div className={styles.empty}>
                    {t('noResults', 'Inga träffar')}
                  </div>
                )}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

export default SanctionsDrawer;
